import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GraduationCap, TrendingUp, Award, AlertCircle, BookOpen, CheckCircle2, XCircle, Clock, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmt = (n) => `${Number(n||0).toLocaleString("fr-FR")} GNF`;

export default function MonEspaceEleve() {
  const [data, setData] = useState(null);
  const [iaUsage, setIaUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/student-portal/my-dashboard");
      setData(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally { setLoading(false); }
    api.get("/sprint-f/ia-usage/me", { params: { days: 30 } }).then(r => setIaUsage(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;
  if (!data) return <div className="p-8">Aucune donnée.</div>;

  const att = data.attendance_last_30;
  const attRate = att.total > 0 ? Math.round((att.present / att.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="mon-espace-eleve">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1e40af] rounded-3xl p-6 text-white">
        <div className="chip mb-3 bg-white/20 border-white/30 text-white"><GraduationCap className="h-3 w-3" /><span>MON ESPACE</span></div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Bonjour {data.student.first_name} !</h1>
        <div className="mt-2 text-white/80">Classe : <strong>{data.student.class_name}</strong></div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI label="Moyenne générale" value={data.average_general.toFixed(2)} icon={TrendingUp} color="text-[#166534]" bg="bg-green-50" suffix="/20" />
        <KPI label="Assiduité" value={`${attRate}%`} icon={CheckCircle2} color="text-[#1E3A8A]" bg="bg-blue-50" />
        <KPI label="Absences" value={att.absent} icon={XCircle} color="text-red-600" bg="bg-red-50" />
        <KPI label="Impayés" value={fmt(data.payments.pending_amount)} icon={Wallet} color="text-[#EA580C]" bg="bg-orange-50" />
      </div>

      {(data.payments.pending_count > 0) && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-orange-900">Paiements en attente</div>
            <div className="text-sm text-orange-800">Vous avez {data.payments.pending_count} paiement(s) en attente pour un total de {fmt(data.payments.pending_amount)}. <Link to="/dashboard/paiements" className="underline font-bold">Voir mes paiements →</Link></div>
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <QuickLink to="/dashboard/cours" title="Cours" desc="Supports pédagogiques" icon={BookOpen} color="bg-purple-600" />
        <QuickLink to="/dashboard/devoirs" title="Devoirs" desc="À rendre" icon={Award} color="bg-[#166534]" />
        <QuickLink to="/dashboard/notes" title="Mes notes" desc={`${data.grades.length} évaluations`} icon={TrendingUp} color="bg-[#1E3A8A]" />
        <QuickLink to="/dashboard/paiements" title="Paiements" desc="Scolarité" icon={Wallet} color="bg-[#EA580C]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Subject averages */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Moyennes par matière</h2>
          {data.subject_averages.length === 0 ? <div className="text-sm text-slate-400">Pas encore de notes.</div> : (
            <div className="space-y-2">
              {data.subject_averages.map((s, i) => {
                const pct = (s.average / 20) * 100;
                const color = s.average >= 14 ? "bg-green-500" : s.average >= 10 ? "bg-blue-500" : "bg-red-500";
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-semibold">{s.subject}</span>
                      <span className="tabular-nums font-bold text-[#1E3A8A]">{s.average}/20</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${color}`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent grades */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Dernières notes</h2>
          {data.grades.length === 0 ? <div className="text-sm text-slate-400">Aucune note.</div> : (
            <div className="space-y-2">
              {data.grades.slice(0, 8).map((g, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                  <div>
                    <div className="font-semibold">{g.subject}</div>
                    <div className="text-xs text-slate-500">{g.type || "Note"}</div>
                  </div>
                  <div className={`font-extrabold tabular-nums ${Number(g.note) >= 10 ? "text-[#166534]" : "text-red-600"}`}>{g.note}/20</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activité IA */}
      {iaUsage && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="ia-usage-card">
          <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Mon activité pédagogique (30 derniers jours)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-blue-50 rounded-lg"><div className="text-2xl font-extrabold text-[#1E3A8A]">{iaUsage.total_activities}</div><div className="text-xs text-slate-500">Activités</div></div>
            <div className="text-center p-3 bg-green-50 rounded-lg"><div className="text-2xl font-extrabold text-[#166534]">{iaUsage.total_time_minutes} min</div><div className="text-xs text-slate-500">Temps d&apos;étude</div></div>
            <div className="text-center p-3 bg-purple-50 rounded-lg"><div className="text-2xl font-extrabold text-purple-700">{iaUsage.completion_rate}%</div><div className="text-xs text-slate-500">Devoirs/quiz terminés</div></div>
            <div className="text-center p-3 bg-orange-50 rounded-lg"><div className="text-sm font-extrabold text-[#EA580C] flex items-center justify-center h-8">{iaUsage.last_active_at ? new Date(iaUsage.last_active_at).toLocaleDateString("fr-FR") : "—"}</div><div className="text-xs text-slate-500">Dernière activité</div></div>
          </div>
        </div>
      )}

      {/* Attendance */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Présences (30 derniers jours)</h2>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 bg-green-50 rounded-lg"><div className="text-2xl font-extrabold text-[#166534]">{att.present}</div><div className="text-xs text-slate-500">Présent</div></div>
          <div className="text-center p-3 bg-red-50 rounded-lg"><div className="text-2xl font-extrabold text-red-600">{att.absent}</div><div className="text-xs text-slate-500">Absent</div></div>
          <div className="text-center p-3 bg-orange-50 rounded-lg"><div className="text-2xl font-extrabold text-[#EA580C]">{att.late}</div><div className="text-xs text-slate-500">Retards</div></div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, bg, suffix }) {
  return (
    <div className={`${bg} rounded-2xl p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div className={`text-2xl font-extrabold ${color} tabular-nums`}>{value}{suffix && <span className="text-sm text-slate-400 font-normal">{suffix}</span>}</div>
    </div>
  );
}

function QuickLink({ to, title, desc, icon: Icon, color }) {
  return (
    <Link to={to} className="block bg-white border border-slate-200 hover:border-[#1E3A8A] rounded-2xl p-4 hover:shadow-lg transition-all group">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center text-white mb-2 group-hover:scale-110 transition-transform`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-heading font-bold text-[#1E3A8A]">{title}</div>
      <div className="text-xs text-slate-500">{desc}</div>
    </Link>
  );
}
